import React, { useMemo, useState } from "react"
import Dialog from "../../dialog/dialog"
import { StaticImage } from "gatsby-plugin-image"
import BodyText from "../../text/body-text/body-text"
import Button from "../../button/button"
import {
  getStateNameFromEnum,
  idStateMapping,
} from "../../MapMarker/centerDetails"
import { graphql, useStaticQuery } from "gatsby"
import { TextField, CustomSelect } from "../utils"
import { useToast } from '../../toast/ToastContext'
import axios from 'axios'
import { Formik, Form } from "formik"
import Joi from "joi"


const commonImageProps = {
  layout: "constrained",
  placeholder: "blurred",
  formats: ["auto", "webp"],
  outputPixelDensities: [0.5, 0.75, 1, 2],
}

const RegistrationForm = ({ isOpen, setIsOpen, clickedFrom, courseId, courseName, sectorId }) => {
  const data = useStaticQuery(graphql`
    query {
      allSector {
        nodes {
          id
          sectorId
          name
        }
      }
      allCourse {
        nodes {
          id
          name
          courseId
          sectorId
        }
      }
    }
  `)
  const { showToast } = useToast() || {}
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allSectors = data?.allSector?.nodes || []
  const allCourses = data?.allCourse?.nodes || []

  const states = useMemo(
    () => Object.values(idStateMapping).map(val => ({
      label: getStateNameFromEnum(val),
      value: val,
    })),
    []
  )

  const sectors = useMemo(
    () => allSectors.map(({ name, sectorId }) => ({ label: name, value: sectorId })).sort((a, b) => a.label.localeCompare(b.label)),
    [allSectors]
  )

  const courses = useMemo(() => [
    ...allCourses.map(({ name, courseId, sectorId }) => ({
      label: typeof window !== "undefined" ? new DOMParser().parseFromString(name, "text/html").body?.textContent || "" : name,
      value: courseId,
      sectorId
    })),
    {
      label: 'Graduate Employability Enhancement Training (GEET)',
      value: '-1',
      sectorId: allSectors.find(sector => sector.name === "Other")?.sectorId
    },
    {
      label: 'Farm Entrepreneurship Development Programme',
      value: '-2',
      sectorId: allSectors.find(sector => sector.name === "Other")?.sectorId
    },
    {
      label: 'General Entrepreneurship Development Programme',
      value: '-3',
      sectorId: allSectors.find(sector => sector.name === "Other")?.sectorId
    }
  ], [allCourses, allSectors])

  const hearAboutUs = [
    { label: "Social-Media", value: "social-media" },
    { label: "Referral", value: "referral" },
    { label: "Google", value: "Google" },
  ]

  const initialValues = {
    name: "",
    phoneNumber: "",
    email: "",
    state: "",
    sector: sectorId || "",
    course: courseName || "",
    qualification: "",
    hear: "",
    clickedFrom,
    courseId: courseId || ""
  }

  const validationSchema = Joi.object({
    name: Joi.string()
      .required().messages({ 'string.empty': 'Full name is required' })
      .pattern(/^(?!.*<script).*$/).message('Script tags are not allowed')
      .pattern(/.*[a-zA-Z].*/).message('Only special characters are not allowed'),
    phoneNumber: Joi.string()
      .required().messages({ 'string.empty': 'Phone number is required' })
      .pattern(/^[0-9+\-]*$/).message('Only numbers, + or - are allowed')
      .min(8).message('Minimum length is 8 digits')
      .max(20).message('Maximum length is 20 digits')
  }).unknown(true)

  const validateWithJoi = values => {
    const { error } = validationSchema.validate(values, { abortEarly: false });
    const errors = {};
    if (error) {
      error.details.forEach(detail => {
        const field = detail.path[0];
        errors[field] = detail.message;
      });
    }
    return errors;
  }

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setIsSubmitting(true);
      await axios.post(`${process.env.GATSBY_API_URL}/learnet.common.db.apis.CommonDbAPIServiceV1/addRegistrationFormData`, values);
      showToast ? showToast("Your request has been sent.", 'success') : alert("Submitted successfully")
      setIsOpen(false);
      resetForm();
    } catch (error) {
      const message = error?.response?.data?.message || "There was an error submitting the form!";
      showToast ? showToast(message, 'error') : alert(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog onClose={() => setIsOpen(false)} isOpen={isOpen} setIsDialogOpen={setIsOpen}>
      <div className="flex flex-col md:flex-row items-stretch">
        <div className="bg-[#3057EE] py-5 px-5 w-full hidden md:w-1/3 md:flex md:flex-col md:items-center md:justify-center md:gap-6">
          <BodyText variant="bodyText5" className="text-white text-center">
            Take the <span className="text-[#FFBD59]">first step</span> to kickstart your career
          </BodyText>
          <StaticImage
            src="../../../images/geet/enquiryForm/rocket.png"
            alt="Rocket illustration - Start your career journey with Geet"
            {...commonImageProps}
          />
        </div>

        <div className="py-5 w-full md:w-2/3 px-8 flex flex-col items-center justify-center">
          <Formik
            initialValues={initialValues}
            validate={validateWithJoi}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, handleBlur, errors, touched }) => {
              const filteredCourses = values.sector ? courses.filter(course => course.sectorId === values.sector) : courses;
              return (
                <Form className="flex flex-col gap-4 w-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField
                      label="Full Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter name"
                      required
                      error={touched.name && !!errors.name}
                      errorMessage={errors.name}
                    />
                    <TextField
                      label="Phone Number"
                      name="phoneNumber"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter phone number"
                      type="phone"
                      required
                      error={touched.phoneNumber && !!errors.phoneNumber}
                      errorMessage={errors.phoneNumber}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CustomSelect
                      required
                      label="State"
                      value={values.state}
                      onChange={option => handleChange({ target: { name: 'state', value: option.value } })}
                      options={states}
                    />
                    <CustomSelect
                      label="Preferred Sector"
                      value={values.sector}
                      onChange={option => handleChange({ target: { name: 'sector', value: option.value } })}
                      options={sectors}
                      disabled={!!sectorId}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {filteredCourses.length ? (
                      <CustomSelect
                        label="Preferred Course"
                        value={values.courseId}
                        onChange={option => handleChange({ target: { name: 'courseId', value: option.value } })}
                        options={filteredCourses}
                        disabled={!!courseId}
                      />
                    ) : (
                      <TextField
                        label="Preferred Course"
                        name="course"
                        value={values.course}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter Course"
                        disabled={!!courseId || !!courseName}
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField
                      label="Educational Qualification"
                      name="qualification"
                      value={values.qualification}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter Educational Qualification"
                    />
                    <CustomSelect
                      label="How did you hear about us?"
                      value={values.hear}
                      onChange={option => handleChange({ target: { name: 'hear', value: option.value } })}
                      options={hearAboutUs}
                    />
                  </div>

                  <div className="text-center mt-6 mx-auto">
                    <Button className="mb-4" buttonProps={{ disabled: isSubmitting, type: 'submit' }}>
                      Submit
                    </Button>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      </div>
    </Dialog>
  )
}

export default RegistrationForm;
